import { FolderGit2, InfinityIcon, Search, Terminal, Waypoints } from "lucide-react"
import { usePostHog } from "posthog-js/react"
import { useEffect, useState, type ComponentType, type CSSProperties } from "react"

import lightspeedSrc from "@/assets/home-new/lightspeed.avif"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import { warmSignInIntent } from "@/lib/navigationWarmup"

import { SiteSection } from "../site/SiteSection"
import { AgentLogoCarousel } from "./AgentLogoCarousel"
import {
  cardRingClassName,
  darkButtonClassName,
  glowRingClassName,
  newBadgeClassName,
} from "./homeNewStyles"
import { HomeNewTrailsCard } from "./HomeNewTrailsCard"
import { HyperspaceCanvas } from "./HyperspaceCanvas"
import { useScrollToProduct } from "./useScrollToProduct"
import { glareClassName, tiltHoverClassName, tiltHoverHandlers } from "./useTiltHover"

const warmupHandlers = {
  onFocus: warmSignInIntent,
  onMouseEnter: warmSignInIntent,
  onTouchStart: warmSignInIntent,
}

function ProductCard({
  Icon,
  title,
  description,
  href,
  product,
  isNew = false,
  className = "",
  children,
  onHoverChange,
  warping,
  ref,
}: {
  Icon: ComponentType<{ size?: number; className?: string; strokeWidth?: number }>
  title: string
  description: string
  href: string
  product: string
  /** Pins a "New" pill to the card's top-right corner. */
  isNew?: boolean
  className?: string
  children?: React.ReactNode
  /** Fires on hover and keyboard focus so the card can light up an effect. */
  onHoverChange?: (hovering: boolean) => void
  /** Plays the card's hover effect without a pointer (touch screens, in view). */
  warping?: boolean
  ref?: React.Ref<HTMLAnchorElement>
}) {
  const scrollToProduct = useScrollToProduct()
  // Only the cards with artwork tilt; the small text-only cards keep the
  // hover scale and pointer glare but don't rotate.
  const tilt = Boolean(children)
  return (
    <a
      ref={ref}
      href={href}
      data-warping={warping ? "true" : undefined}
      onClick={href.startsWith("#") ? (event) => scrollToProduct(event, href.slice(1)) : undefined}
      data-product={product}
      data-tilt="small"
      style={tilt ? undefined : ({ "--card-max-tilt": 0 } as CSSProperties)}
      onPointerEnter={onHoverChange ? () => onHoverChange(true) : undefined}
      onPointerMove={tiltHoverHandlers.onPointerMove}
      onPointerLeave={(event) => {
        tiltHoverHandlers.onPointerLeave(event)
        onHoverChange?.(false)
      }}
      onFocus={onHoverChange ? () => onHoverChange(true) : undefined}
      onBlur={onHoverChange ? () => onHoverChange(false) : undefined}
      className={`flex min-h-[94px] flex-col overflow-hidden rounded-lg bg-floating focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus ${tiltHoverClassName} ${cardRingClassName} ${className}`}
    >
      {children}
      <div className="flex items-start gap-3 p-4">
        <Icon
          size={20}
          strokeWidth={1.33}
          className="mt-px shrink-0 text-default"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-col gap-0.5 leading-5">
          {/* With a "New" badge the title wraps before it instead of running under it. */}
          <p
            className={`font-sans text-base font-medium tracking-[0.01em] text-default ${isNew ? "pr-10" : ""}`}
          >
            {title}
          </p>
          <p className="font-sans text-sm tracking-[-0.01em] text-muted">{description}</p>
        </div>
      </div>
      {isNew && <span className={newBadgeClassName}>New</span>}
      <span className={glareClassName} aria-hidden="true" />
    </a>
  )
}

/**
 * On screens without hover (phones, tablets), plays the Code hosting card's
 * light-speed effect from the moment the card enters the bottom of the screen
 * until it leaves, since there is no pointer to start it. Pointer devices keep
 * it on hover only.
 */
function useAutoWarpInView() {
  const [element, setElement] = useState<HTMLAnchorElement | null>(null)
  const [active, setActive] = useState(false)

  const prefersReducedMotion = usePrefersReducedMotion()
  useEffect(() => {
    if (!element) return
    if (!window.matchMedia("(hover: none)").matches) return
    if (prefersReducedMotion) return
    const observer = new IntersectionObserver(
      ([entry]) => setActive(Boolean(entry?.isIntersecting)),
      // Starts as soon as any part of the card enters the screen, and keeps
      // going until it has scrolled out of view.
      { threshold: 0 },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [element, prefersReducedMotion])

  // Drops as soon as reduced motion is switched on, even mid-warp.
  return { attach: setElement, active: active && !prefersReducedMotion }
}

export function HomeNewHero() {
  const posthog = usePostHog()
  const [hovering, setHovering] = useState(false)
  const { attach: attachAutoWarp, active: autoWarping } = useAutoWarpInView()
  const warping = hovering || autoWarping

  return (
    <SiteSection
      fullWidth
      borderBottom={false}
      outerClassName="home-new-hero relative isolate"
      innerClassName="mx-auto flex max-w-320 flex-col gap-12 px-6 pt-12 pb-16 sm:gap-24 sm:px-10 sm:pt-24 min-[1360px]:px-0"
    >
      {/* Phones read headline, paragraph, Get Started, then the agent logos.
          From sm up the button and logos sit together under the headline,
          with the paragraph below (sm–lg) or in the right column (lg+). */}
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:gap-x-[60px]">
        <h1 className="max-w-[552px] font-headline text-[40px] leading-none font-medium text-default sm:text-5xl lg:col-start-1 lg:row-start-1">
          The Git forge for agent-human collaboration
        </h1>
        <p className="max-w-[552px] font-sans text-base leading-6 text-muted sm:order-2 sm:mt-4 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mt-0 lg:self-start">
          Host, review, and collaborate with agents in a new distributed Git forge that stores your
          intent alongside your code, built for the scale and throughput of agentic coding.
        </p>
        <div className="flex flex-col items-start gap-10 sm:order-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-3 lg:col-start-1 lg:row-start-2">
          <a
            href="/login"
            className={darkButtonClassName}
            {...warmupHandlers}
            onClick={() => posthog.capture("hero_cta_clicked")}
          >
            Get Started
          </a>
          <span className="flex items-center gap-6 font-sans text-sm leading-5 text-muted">
            Works with any agent
            <AgentLogoCarousel />
          </span>
        </div>
      </div>

      {/* Bento: Trails leads (full width below 1120px, two of four columns
          above), then the three small cards and the Code hosting + Continuous
          Integration pair as two even columns. Four columns from 1120px: below it
          the small cards get too narrow for their copy. */}
      <div className="bento:min-h-[330px] bento:grid-cols-4 grid gap-2 md:grid-cols-2 md:gap-6">
        <HomeNewTrailsCard />

        <div className="grid gap-2 md:grid-rows-3 md:gap-6">
          <ProductCard
            Icon={Waypoints}
            title="Continuous Memory"
            description="Turn your repo into a multi-agent brain"
            href="#continuous-memory"
            product="continuous-memory"
          />
          <ProductCard
            Icon={Search}
            title="Agentic Search"
            description="Search code, commits and sessions in one query"
            href="#agentic-search"
            product="agentic-search"
          />
          <ProductCard
            Icon={Terminal}
            title="Entire CLI"
            description="Capture every agent session from your terminal"
            href="/features/cli"
            product="cli"
          />
        </div>

        <div className="grid gap-2 md:grid-rows-3 md:gap-6">
          <ProductCard
            Icon={FolderGit2}
            title="Code Hosting"
            description="Host your repos on a distributed Git network"
            href="#code-hosting"
            product="code-hosting"
            onHoverChange={setHovering}
            warping={autoWarping}
            ref={attachAutoWarp}
            className={`md:row-span-2 ${glowRingClassName}`}
          >
            <div
              className="relative min-h-[118px] w-full flex-1 overflow-hidden rounded-t-lg"
              aria-hidden="true"
            >
              {/* The art takes whatever height the card has left over the copy,
                  so the card never shows an empty gap when its row runs tall.
                  The planet horizon, mirrored as in the design. On hover it zooms
                  toward the top of the image over 12s, so the horizon drops away
                  and the view heads into space with the light-speed streaks; it
                  eases back over 1.6s when the pointer leaves. */}
              <img
                src={lightspeedSrc}
                alt=""
                width={2332}
                height={1000}
                className="absolute bottom-0 left-1/2 h-[calc(100%+68px)] w-full max-w-none min-w-[361px] origin-top -translate-x-1/2 -scale-x-100 object-cover object-bottom transition-transform duration-1600 ease-in-out group-hover:scale-x-[-2.5] group-hover:scale-y-[2.5] group-hover:duration-12000 group-hover:ease-linear group-data-[warping=true]:scale-x-[-2.5] group-data-[warping=true]:scale-y-[2.5] group-data-[warping=true]:duration-12000 group-data-[warping=true]:ease-linear motion-reduce:transition-none motion-reduce:group-hover:scale-x-[-1] motion-reduce:group-hover:scale-y-100"
                loading="eager"
                decoding="async"
              />
              {/* Light-speed streaks while the card is hovered. */}
              <HyperspaceCanvas active={warping} originX={0.5} originY={0.55} />
              <span className="absolute inset-x-0 bottom-0 flex h-6 items-center justify-center truncate bg-[color-mix(in_srgb,var(--home-new-badge)_14%,white)] px-2 pb-px font-sans text-xs leading-[15px] font-medium text-(--home-new-badge) dark:bg-[color-mix(in_srgb,var(--home-new-badge)_45%,black)] dark:text-white">
                Up to 89× faster than the competition
              </span>
            </div>
          </ProductCard>
          <ProductCard
            Icon={InfinityIcon}
            title="Continuous Integration"
            description="Native integrations with agent-scale CI providers"
            href="#ci"
            product="ci"
            // The New badge is hidden for now: with it the title wraps below 1310px.
          />
        </div>
      </div>
    </SiteSection>
  )
}
